<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="text-center">Управление адресами (Excel)</h1>
      </v-col>
    </v-row>

    <!-- Карточка загрузки Excel -->
    <v-card elevation="3" class="pa-5 mt-4">
      <v-card-title>Загрузка Excel</v-card-title>
      <v-card-subtitle>Укажите дату и выберите Excel-файл для импорта</v-card-subtitle>

      <v-form @submit.prevent="onUpload">
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="selectedDate"
              label="Дата"
              type="date"
              required
              :disabled="loading"
            />
          </v-col>
          <v-col cols="12" md="8">
            <v-file-input
              v-model="file"
              label="Excel файл"
              required
              :disabled="loading"
            />
          </v-col>
        </v-row>

        <v-btn type="submit" color="primary" :loading="loading">
          Загрузить
        </v-btn>
      </v-form>

      <!-- Прогресс -->
      <v-progress-linear
        v-if="loading"
        :value="uploadProgress"
        color="blue"
        height="8"
        class="mt-3"
      >
        {{ uploadProgress }}%
      </v-progress-linear>

      <!-- Уведомления -->
      <v-alert
        v-if="successMessage"
        type="success"
        class="mt-3"
      >
        {{ successMessage }}
      </v-alert>

      <v-alert
        v-if="errorMessage"
        type="error"
        class="mt-3"
      >
        {{ errorMessage }}
      </v-alert>
    </v-card>

    <!-- Если есть multipleCandidates, показываем таблицу для «уточнения» -->
    <v-card
      v-if="multipleCandidates.length"
      elevation="2"
      class="pa-4 mt-5"
    >
      <v-card-title>Неоднозначные адреса (требуют уточнения)</v-card-title>

      <v-data-table
        :headers="candidateHeaders"
        :items="multipleCandidates"
        item-key="rawAddress"
        class="elevation-1"
      >
        <!-- Переименованный слот: itemActions -->
        <template slot="item.actions" slot-scope="{ item }">
          <v-btn small color="primary" @click="openCandidateDialog(item)">
            Уточнить
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Если есть problemAddresses -->
    <v-card
      v-if="problemAddresses.length"
      elevation="2"
      class="pa-4 mt-5"
    >
      <v-card-title>Проблемные адреса (не нашлись даже через Google)</v-card-title>
      <v-data-table
        :headers="problemHeaders"
        :items="problemAddresses"
        item-key="address"
        class="elevation-1"
      />
    </v-card>

    <!-- Диалог с вариантами для одного "спорного" адреса -->
    <v-dialog
      v-model="dialogOpen"
      max-width="800px"
    >
      <v-card>
        <v-card-title>Уточнение адреса</v-card-title>
        <v-card-subtitle>
          {{ currentItem?.rawAddress }}
        </v-card-subtitle>

        <v-card-text>
          <v-list v-if="currentItem?.candidates && currentItem.candidates.length">
            <v-list-item
              v-for="(cand, i) in currentItem.candidates"
              :key="i"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ cand.formatted_address }}
                  <span v-if="cand.partial_match"> (partial_match)</span>
                </v-list-item-title>
                <v-list-item-subtitle>
                  [{{ cand.lat }}, {{ cand.lon }}]
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small color="green darken-1" @click="confirmCandidate(currentItem, cand)">
                  Принять
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-else>Нет вариантов?</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="red" @click="dialogOpen = false">
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { api } from '../lib/api'

export default {
  name: "AdminDashboard",
  data() {
    return {
      selectedDate: "",
      file: null,
      loading: false,
      uploadProgress: 0,

      successMessage: "",
      errorMessage: "",

      multipleCandidates: [],
      problemAddresses: [],

      dialogOpen: false,
      currentItem: null,

      // Изменили value: 'itemActions' (без точки!)
      candidateHeaders: [
        { text: "Адрес", value: "rawAddress" },
        { text: "Кол-во", value: "count" },
        { text: "Действия", value: "actions", sortable: false },
      ],
      problemHeaders: [
        { text: "Адрес", value: "address" },
        { text: "Кол-во", value: "count" },
      ],
    };
  },
  methods: {
    async onUpload() {
      if (!this.selectedDate || !this.file) {
        this.errorMessage = "Укажите дату и файл";
        return;
      }
      this.errorMessage = "";
      this.successMessage = "";
      this.multipleCandidates = [];
      this.problemAddresses = [];
      this.loading = true;
      this.uploadProgress = 0;

      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("date", this.selectedDate);

      try {
        const resp = await api.post('/uploadExcel', formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.lengthComputable) {
              this.uploadProgress = Math.round((e.loaded / e.total) * 100);
            }
          },
        });

        if (resp.data.success) {
          this.successMessage = "Файл успешно загружен";
          if (resp.data.multipleCandidates) {
            this.multipleCandidates = resp.data.multipleCandidates;
          }
          if (resp.data.problemAddresses) {
            this.problemAddresses = resp.data.problemAddresses;
          }
        } else {
          this.errorMessage = "Произошла ошибка при загрузке";
        }
      } catch (err) {
        console.error("uploadExcel error:", err);
        this.errorMessage = "Ошибка загрузки файла";
      } finally {
        this.loading = false;
      }
    },

    openCandidateDialog(item) {
      this.currentItem = item;
      this.dialogOpen = true;
    },

    async confirmCandidate(item, candidate) {
      try {
        const payload = {
          date: this.selectedDate,
          address: candidate.formatted_address,
          lat: candidate.lat,
          lon: candidate.lon,
          count: item.count,
        };
        const res = await api.post('/confirmCandidate', payload)
        if (res.data.success) {
          const idx = this.multipleCandidates.indexOf(item);
          if (idx >= 0) this.multipleCandidates.splice(idx, 1);
          this.dialogOpen = false;
        } else {
          this.errorMessage = "Ошибка при подтверждении адреса";
        }
      } catch (err) {
        console.error("confirmCandidate error:", err);
        this.errorMessage = "Ошибка сети при подтверждении";
      }
    },
  },
};
</script>

<style scoped>
.v-btn {
  font-size: 16px;
  font-weight: bold;
}

.v-card {
  max-width: 1000px;
  margin: 20px auto;
}

h1 {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 20px;
}
</style>
